import { Chip, ChipProps, Stack, alpha, styled } from '@mui/material';
import { ChipCloseIcon } from '@synergy/theme-next';
import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement, ReactNode } from 'react';

const StyledChip = styled(Chip)(({ theme }) => ({
    ...theme.typography.caption,
    height: theme.spacing(3.5),
    cursor: 'pointer',
}));

const StyledChipCloseIcon = styled(ChipCloseIcon)(({ theme }) => ({
    '&.MuiChip-deleteIcon': {
        fontSize: 16,
        color: alpha(theme.palette.common.white, 0.6),

        '&:hover': {
            color: theme.palette.common.white,
        },
    },
}));

export interface FilterChipProps extends ChipProps {
    filterParam?: FilterChipParams;
    value?: string;
    onChipDelete: () => void;
}

export enum FilterChipParams {
    AttributeKeys = 'attributeKeys',
    CategoryId = 'categoryIds',
    SupplierId = 'supplierIds',
    Bestprice = 'isBestPrice',
    SpecialPrice = 'hasSpecialPrice',
    MinPrice = 'minPrice',
    MaxPrice = 'maxPrice',
    SpecialOffer = 'specialOffer',
    EcoProduct = 'isEcoProduct',
}

export const FilterChip = (props: FilterChipProps): ReactElement => {
    const { filterParam, value, onChipDelete, ...chipProps } = props;

    const translationObject: { [Key in FilterChipParams]: string } = {
        [FilterParams.BestPrice]: 'app.filter.bestprice',
        [FilterParams.CategoryId]: 'app.filter.categoryId',
        [FilterParams.MaxPrice]: 'app.filter.price',
        [FilterParams.MinPrice]: 'app.filter.price',
        [FilterParams.EcoProduct]: 'app.filter.ecoProduct',
        [FilterParams.SpecialOffer]: 'app.filter.specialOffer',
        [FilterParams.SupplierIds]: 'app.filter.supplierId',
        [FilterParams.AttributeKeys]: 'app.filter.attributeKeys',
        [FilterParams.SpecialPrice]: 'app.filter.specialPrice',
    };

    const getLabel = (): ReactNode => {
        if (!filterParam) return chipProps.label;

        if (value === 'true') {
            return <FormattedMessage id={translationObject[filterParam]} />;
        }

        if (filterParam === FilterChipParams.AttributeKeys) {
            return (
                <Stack direction="row" gap={0.5}>
                    <strong>{value}</strong>
                    <span>{value}</span>
                </Stack>
            );
        }

        return (
            <Stack direction="row" gap={0.5}>
                <strong>
                    <FormattedMessage id={translationObject[filterParam]} />
                </strong>
                <span>{value}</span>
            </Stack>
        );
    };

    return (
        <StyledChip
            label={getLabel()}
            color="primary"
            onDelete={onChipDelete}
            deleteIcon={<StyledChipCloseIcon />}
            sx={{ cursor: 'unset' }}
            {...chipProps}
        />
    );
};
